import { useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

export const useHelpdeskBulkActions = (
  handleMoveToCompletion: (ticketId: number) => Promise<void>,
  handleRevertToOpen: (ticketId: number) => Promise<void>,
  refreshTickets: () => Promise<void>,
  onExcelExport?: () => void,
) => {
  const { session } = useSession();

  // Selection state for bulk actions
  const [selectedOpenTickets, setSelectedOpenTickets] = useState<Set<number>>(
    new Set(),
  );
  const [selectedCompletionTickets, setSelectedCompletionTickets] = useState<
    Set<number>
  >(new Set());

  // Bulk action handlers
  const handleBulkMoveToCompletion = async () => {
    const ticketIds = Array.from(selectedOpenTickets);
    if (ticketIds.length === 0) return;

    try {
      const movePromises = ticketIds.map((id) => handleMoveToCompletion(id));
      await Promise.all(movePromises);
      setSelectedOpenTickets(new Set());
      toast.success(
        `${ticketIds.length} ticket${ticketIds.length > 1 ? "s" : ""} verplaatst naar afrondingsfase`,
      );
    } catch (error) {
      console.error("Error moving tickets to completion:", error);
      toast.error("Fout bij verplaatsen van tickets naar afrondingsfase");
    }
  };

  const handleBulkRevertToOpen = async () => {
    const ticketIds = Array.from(selectedCompletionTickets);
    if (ticketIds.length === 0) return;

    try {
      const revertPromises = ticketIds.map((id) => handleRevertToOpen(id));
      await Promise.all(revertPromises);
      setSelectedCompletionTickets(new Set());
      toast.success(
        `${ticketIds.length} ticket${ticketIds.length > 1 ? "s" : ""} teruggezet naar openstaand`,
      );
    } catch (error) {
      console.error("Error reverting tickets to open:", error);
      toast.error("Fout bij terugzetten van tickets naar openstaand");
    }
  };

  const handleBulkDelete = async () => {
    const openTicketIds = Array.from(selectedOpenTickets);
    const completionTicketIds = Array.from(selectedCompletionTickets);
    const allTicketIds = [...openTicketIds, ...completionTicketIds];

    if (allTicketIds.length === 0) return;

    const confirmed = window.confirm(
      `Ben je zeker dat je ${allTicketIds.length} ticket${allTicketIds.length > 1 ? "s" : ""} wilt verwijderen?`,
    );
    if (!confirmed) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets/bulk`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ticketIds: allTicketIds }),
        },
        session,
      );

      if (response.ok) {
        const result = await response.json();
        setSelectedOpenTickets(new Set());
        setSelectedCompletionTickets(new Set());

        if (result.deletedTicketIds.length > 0) {
          const emailsText =
            result.totalDeletedEmails > 0
              ? ` (inclusief ${result.totalDeletedEmails} email${result.totalDeletedEmails > 1 ? "s" : ""})`
              : "";
          toast.success(
            `${result.deletedTicketIds.length} ticket${result.deletedTicketIds.length > 1 ? "s" : ""} succesvol verwijderd${emailsText}`,
          );
        }

        // Refresh tickets
        await refreshTickets();
      }
    } catch (error) {
      console.error("Error deleting tickets:", error);
      toast.error("Fout bij het verwijderen van tickets");
    }
  };

  const clearAllSelections = () => {
    setSelectedOpenTickets(new Set());
    setSelectedCompletionTickets(new Set());
  };

  // Bulk actions configuration for FilterBar (icons handled in component)
  const getBulkActions = (onBulkPhaseChange?: () => void) => {
    const totalCount = selectedOpenTickets.size + selectedCompletionTickets.size;
    const hasSelection = totalCount > 0;

    return [
      {
        label: `Wijzig fase (${totalCount})`,
        iconType: "changePhase" as const,
        onClick: onBulkPhaseChange || (() => {}),
        variant: "primaryMoss" as const,
        disabled: !hasSelection,
      },
      {
        label: `Naar afrondingsfase (${selectedOpenTickets.size})`,
        iconType: "moveToCompletion" as const,
        onClick: handleBulkMoveToCompletion,
        variant: "blue" as const,
        disabled: selectedOpenTickets.size === 0,
      },
      ...(selectedCompletionTickets.size > 0
        ? [
            {
              label: `Terug naar openstaand (${selectedCompletionTickets.size})`,
              iconType: "revertToOpen" as const,
              onClick: handleBulkRevertToOpen,
              variant: "yellow" as const,
              disabled: false,
            },
          ]
        : []),
      ...(onExcelExport
        ? [
            {
              label: `Exporteer naar Excel (${totalCount})`,
              iconType: "export" as const,
              onClick: onExcelExport,
              variant: "secondaryNeutral" as const,
              disabled: false,
            },
          ]
        : []),
      {
        label: `Verwijder (${totalCount})`,
        iconType: "delete" as const,
        onClick: handleBulkDelete,
        variant: "red" as const,
        disabled: !hasSelection,
      },
    ];
  };

  return {
    selectedOpenTickets,
    selectedCompletionTickets,
    setSelectedOpenTickets,
    setSelectedCompletionTickets,
    handleBulkMoveToCompletion,
    handleBulkRevertToOpen,
    handleBulkDelete,
    clearAllSelections,
    getBulkActions,
    selectedCount: selectedOpenTickets.size + selectedCompletionTickets.size,
  };
};
